import { CheckCircle2, Clock, Phone, Utensils, Package } from 'lucide-react';
import { useLocale } from '../../lib/locale';

interface OrderSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderNumber: string;
  estimatedTime: string;
  restaurantName: string;
  totalAmount: number;
  paymentMethod: string;
  onTrackOrder?: () => void;
}

export function OrderSuccessModal({
  isOpen,
  onClose,
  orderNumber,
  estimatedTime,
  restaurantName,
  totalAmount,
  paymentMethod,
  onTrackOrder,
}: OrderSuccessModalProps) {
  const { formatMoney } = useLocale();
  if (!isOpen) return null;

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'card':
        return 'Card Payment';
      case 'upi':
        return 'UPI Payment';
      case 'wallet':
        return 'Wallet Payment';
      case 'cash':
        return 'Cash on Delivery';
      case 'netbanking':
        return 'Net Banking';
      default:
        return method;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-0 sm:p-4">
      <div className="bg-white shadow-2xl w-full sm:rounded-2xl overflow-hidden max-w-lg h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col">
        {/* Success Header */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 text-center sm:p-8 shrink-0 sm:rounded-t-2xl">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-lg sm:h-20 sm:w-20">
            <CheckCircle2 className="h-10 w-10 text-green-600 sm:h-12 sm:w-12" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Order Placed Successfully!</h2>
          <p className="text-green-100">Your delicious food is on its way</p>
        </div>

        {/* Order Details */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5 sm:space-y-6">
          {/* Order Number */}
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <p className="text-sm text-gray-600 mb-1">Order Number</p>
            <p className="text-xl font-bold text-gray-900 tracking-wider break-all sm:text-2xl">{orderNumber}</p>
          </div>

          {/* Details Grid */}
          <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Utensils className="w-5 h-5 text-blue-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 leading-tight">Restaurant</p>
                <p className="font-semibold text-gray-900 truncate">{restaurantName}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 px-4 py-3">
              <div className="h-10 w-10 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 leading-tight">Estimated Delivery Time</p>
                <p className="font-semibold text-gray-900 truncate">{estimatedTime}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 px-4 py-3">
              <div className="h-10 w-10 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-green-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 leading-tight">Payment Method</p>
                <p className="font-semibold text-gray-900 truncate">{getPaymentMethodLabel(paymentMethod)}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 px-4 py-3">
              <div className="h-10 w-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-purple-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 leading-tight">Total Amount</p>
                <p className="text-xl font-bold text-gray-900">{formatMoney(totalAmount)}</p>
              </div>
            </div>
          </div>

          {/* Status Timeline */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Order Status</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Order Placed</p>
                  <p className="text-xs text-gray-600">Your order has been confirmed</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Preparing Food</p>
                  <p className="text-xs text-gray-500">We'll notify you when it's ready</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Out for Delivery</p>
                  <p className="text-xs text-gray-500">Track your order in real-time</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="shrink-0 p-4 sm:p-6 pt-0 space-y-3 bg-white">
          <button
            onClick={onTrackOrder || onClose}
            className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            Track Order
          </button>
          <button
            onClick={onClose}
            className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Back to Menu
          </button>

          {/* Info */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-xs text-gray-700 text-center">
              <span className="font-semibold">Need Help?</span> Contact the restaurant or our support team for any assistance
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
